// client/src/types/issue-triage.ts
import { Timestamp } from 'firebase/firestore';

// ============================================================================
// ISSUE REPORT STATUS & CLASSIFICATION
// ============================================================================

export type IssueStatus =
  | 'new'           // Just submitted, awaiting analysis
  | 'analyzing'     // AI is analyzing
  | 'needs_review'  // Analysis complete, awaiting developer review
  | 'reviewed'      // Pickup queue: owned + direction given, NOT yet put into Claude
  | 'in_progress'   // Picked up (Copy & Start) - pickedUpBy says who
  | 'done'          // Fix verified in production
  | 'dismissed';    // Marked as non-issue

/**
 * A person stamped onto an issue: uid + the owner-config name (or the login
 * email for non-owners) — server-derived, never users.displayName.
 */
export interface IssuePerson {
  uid: string;
  name: string;
  /** Set when an agent acted for this user. */
  via?: 'agent';
}

/** A file added to an issue after it was submitted. Type, size and path are read from the Storage object by the server; `name` is the staff member's own label (cleaned, at most 200 characters). */
export interface IssueAttachment {
  url: string;
  path: string;
  name: string;
  contentType: string;
  size: number;
  addedBy: IssuePerson;
  addedAt: Timestamp | Date | null;
}

export interface IssueRemovedAttachment {
  /** The link that was STORED on the issue (not the one the caller sent), kept so a removal can be undone. */
  url: string | null;
  /** The Storage object path. */
  path?: string | null;
  name: string | null;
  from: 'attachments' | 'attachmentUrls' | 'screenshotUrl';
  by: IssuePerson;
  at: Timestamp | Date | null;
}

export type IssueClassification = 'bug' | 'non_issue' | 'feature_request';

export type IssueType = 'bug' | 'suggestion' | 'question';

export type IssueSeverity = 'critical' | 'high' | 'medium' | 'low';

export type IssueSource = 'user_reported' | 'auto_captured';

export type NonIssueReason =
  | 'intentional_design'
  | 'user_misunderstanding'
  | 'feature_request'
  | 'cannot_reproduce';

export type ConfidenceLevel = 'high' | 'medium' | 'low';

// ============================================================================
// CAPTURED CONTEXT
// ============================================================================

export interface ConsoleLogEntry {
  level: 'log' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: number;
  stack?: string;
}

export interface ClickTrailEntry {
  element: string;      // CSS selector-like description
  text: string;         // Button/link text (truncated)
  timestamp: number;
  path: string;         // Route at time of click
}

export interface NetworkRequestEntry {
  method: string;
  url: string;
  status: number;
  duration: number;
  requestBody?: string;   // Truncated, sanitized
  responseBody?: string;  // Truncated, sanitized
  timestamp: number;
}

export interface LongTaskEntry {
  duration: number;
  timestamp: number;
}

export interface CapturedContext {
  // Location
  url: string;
  route: string;
  navigationHistory: string[];  // Last 10 routes

  // Environment
  browser: string;
  os: string;
  viewport: { width: number; height: number };
  scrollPosition: { x: number; y: number };

  // User
  userId: string;
  email: string;
  role: string;
  permissions: string[];

  // Logs & Events
  consoleLogs: ConsoleLogEntry[];        // Last 100
  clickTrail: ClickTrailEntry[];         // Last 20
  networkRequests: NetworkRequestEntry[]; // Last 30

  // App State
  reactQueryCacheKeys: string[];  // Keys only (not values)
  localStorageKeys: string[];     // Keys only

  // Performance
  memoryUsage?: number;
  longTasks: LongTaskEntry[];

  // Navigation state (ISS-544 diagnostic): when a page loses its tabs, which of "no user",
  // "no current module" or "no nav items" was the empty one. Ids and counts only, no user data.
  navState?: NavStateSnapshot | null;
}

export interface NavStateSnapshot {
  /** The module id the app thinks it is in (e.g. 'P12'); null when it has none. */
  currentModule: string | null;
  /** How many tabs the module's navigation holds. */
  navItemCount: number;
  /** Whether the app has a signed-in user object. */
  hasUser: boolean;
  /** The route before the current one; null when this is the first. */
  previousRoute: string | null;
}

// ============================================================================
// SELECTED ELEMENT (for element picker mode)
// ============================================================================

export interface SelectedElement {
  selector: string;
  tagName: string;
  className: string;
  textContent: string;
  boundingRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

// ============================================================================
// ANNOTATION DATA
// ============================================================================

export interface AnnotationData {
  type: 'drawing' | 'element';
  data: unknown;  // Canvas drawing data or element selection
}

// ============================================================================
// ISSUE REPORT (Firestore document)
// ============================================================================

export interface IssueReport {
  // Identity
  id: string;        // Firestore document ID
  reportId?: string; // Legacy alias for id
  /** Short sequential code for easy reference, e.g., ISS-042 */
  issueCode?: string;
  source: IssueSource;
  groupHash?: string;       // For auto-captured: groups recurring errors
  groupedCount?: number;    // Occurrence count for grouped errors

  // Status & Classification
  status: IssueStatus;
  issueType: IssueType;     // User-selected type: bug, suggestion, question
  classification: IssueClassification | null;
  severity: IssueSeverity | null;

  // The Issue
  title: string;
  description: string;
  screenshotUrl?: string;
  /** The reporter's own images, alongside (or instead of) the capture — ISS-485. */
  attachmentUrls?: string[];
  annotationData?: AnnotationData;
  selectedElement?: SelectedElement;

  // Developer direction (added during review)
  developerNotes?: string;
  developerNotesUpdatedAt?: Timestamp;
  /** Who saved the direction — server-derived from auth, never client-supplied. */
  developerNotesBy?: IssuePerson;

  // Ownership: one of the issue-triage developers (issueTriage_listOwners).
  owner?: IssuePerson;
  ownerUpdatedBy?: IssuePerson;
  ownerUpdatedAt?: Timestamp;

  // Starter: set on any move into in_progress from another stage (Copy & Start,
  // a drag, a reopen) — who started it, and when. Server-derived.
  pickedUpBy?: IssuePerson;
  pickedUpAt?: Timestamp;
  /** Earlier starters kept when a reopen overwrote pickedUpBy (last 20, oldest first). Audit only. */
  pickedUpHistory?: { by: IssuePerson; at: Timestamp | null }[];

  /** Files and screenshots a team member added after submit (server-validated; see issueTriage_addAttachment). */
  attachments?: IssueAttachment[];
  /** Audit trail of files taken off the issue (last 50). The Storage objects are kept. */
  removedAttachments?: IssueRemovedAttachment[];

  // Captured Context
  context: CapturedContext;

  // Who reported
  reportedBy: {
    userId: string;
    email: string;
  };

  // AI Analysis (populated by analyzeIssue Cloud Function - Phase 2)
  analysis?: IssueAnalysis;

  // Approvals
  approvals: IssueApproval[];

  // Implementation (populated by GitHub Action - Phase 4)
  implementation?: IssueImplementation;

  // Deployment tracking (Phase 5)
  deployment?: IssueDeployment;

  /** Chronological status notes / comment thread */
  statusNotes?: Array<{
    text: string;
    author: string;
    authorId: string;
    timestamp: Timestamp;
  }>;

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface IssueAnalysis {
  reasoning: string;
  classification: IssueClassification;
  severity: IssueSeverity;
  rootCause: string;
  affectedFiles: string[];
  proposedApproach: string;
  isNonIssue: boolean;
  nonIssueReason?: NonIssueReason;
  confidence: ConfidenceLevel;
  analyzedAt: Timestamp;
}

export interface IssueApproval {
  stage: 'approach' | 'implementation' | 'merge' | 'verification';
  approvedBy: string;
  timestamp: Timestamp;
  notes?: string;
}

export interface IssueImplementation {
  branchName?: string;
  prUrl?: string;
  prNumber?: number;

  // Status timestamps
  triggeredAt?: Timestamp;
  completedAt?: Timestamp;
  failedAt?: Timestamp;
  errorAt?: Timestamp;

  // Results
  buildPassed?: boolean;
  testsPassed?: boolean;
  error?: string;

  // Detailed results (Phase 5)
  codeChanges?: string;
  codeReviewResults?: string;
  testResults?: {
    passed: number;
    failed: number;
    output: string;
  };
  buildOutput?: string;
  mockups?: Array<{
    title: string;
    description: string;
    imageUrl: string;
  }>;
}

export interface IssueDeployment {
  commitSha: string;
  mergedAt: Timestamp;
  vercelDeploymentId?: string;
  vercelStatus: 'pending' | 'building' | 'ready' | 'error';
  vercelUrl?: string;
  vercelBuildLogs?: string;
  fixAttempts: Array<{
    commitSha?: string;
    attemptNumber?: number;
    error?: string;
    success?: boolean;
    timestamp: Timestamp;
  }>;
}

// ============================================================================
// INPUT TYPES (for creating/updating)
// ============================================================================

export interface SubmitBugReportInput {
  title: string;
  description: string;
  issueType: IssueType;         // User-selected: bug, suggestion, question
  severity?: IssueSeverity;     // User-selected severity (for bugs)
  screenshotDataUrl?: string;   // Base64 data URL
  /** The reporter's own images, alongside or instead of the capture (ISS-485). */
  attachmentDataUrls?: string[];
  requestId?: string;
  annotationData?: AnnotationData;
  selectedElement?: SelectedElement;
  context: CapturedContext;
}

// ============================================================================
// FEATURE FLAGS
// ============================================================================

export interface IssueTriageFeatureFlags {
  contextCaptureEnabled: boolean;
  autoAnalysisEnabled: boolean;
  maxIssuesPerHour: number;
}

export const DEFAULT_ISSUE_TRIAGE_FLAGS: IssueTriageFeatureFlags = {
  contextCaptureEnabled: true,
  autoAnalysisEnabled: true,
  maxIssuesPerHour: 50,
};

// ============================================================================
// STATUS CONFIG (for UI)
// ============================================================================

export const ISSUE_STATUS_CONFIG: Record<IssueStatus, {
  label: string;
  color: string;
  textColor: string;
  bgColor: string;
}> = {
  new: { label: 'New', color: '#6b7280', textColor: 'text-slate-600', bgColor: 'bg-slate-100' },
  analyzing: { label: 'Analyzing', color: '#3b82f6', textColor: 'text-blue-600', bgColor: 'bg-blue-100' },
  needs_review: { label: 'Needs Review', color: '#f59e0b', textColor: 'text-amber-600', bgColor: 'bg-amber-100' },
  reviewed: { label: 'Reviewed', color: '#3b82f6', textColor: 'text-blue-600', bgColor: 'bg-blue-100' },
  in_progress: { label: 'In Progress', color: '#8b5cf6', textColor: 'text-purple-600', bgColor: 'bg-purple-100' },
  done: { label: 'Done', color: '#059669', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50' },
  dismissed: { label: 'Dismissed', color: '#9ca3af', textColor: 'text-slate-500', bgColor: 'bg-slate-100' },
};

export const ISSUE_SEVERITY_CONFIG: Record<IssueSeverity, {
  label: string;
  color: string;
  emoji: string;
  textColor: string;
  bgColor: string;
}> = {
  critical: { label: 'Critical', color: '#dc2626', emoji: '🔴', textColor: 'text-red-700', bgColor: 'bg-red-100' },
  high: { label: 'High', color: '#f97316', emoji: '🟠', textColor: 'text-orange-700', bgColor: 'bg-orange-100' },
  medium: { label: 'Medium', color: '#f59e0b', emoji: '🟡', textColor: 'text-amber-700', bgColor: 'bg-amber-100' },
  low: { label: 'Low', color: '#3b82f6', emoji: '🔵', textColor: 'text-blue-700', bgColor: 'bg-blue-100' },
};

export const ISSUE_TYPE_CONFIG: Record<IssueType, {
  label: string;
  description: string;
  emoji: string;
  textColor: string;
  bgColor: string;
}> = {
  bug: { label: 'Bug', description: 'Something is broken or not working as expected', emoji: '🐛', textColor: 'text-red-700', bgColor: 'bg-red-100' },
  suggestion: { label: 'Suggestion', description: 'An idea for improvement or new feature', emoji: '💡', textColor: 'text-amber-700', bgColor: 'bg-amber-100' },
  question: { label: 'Question', description: 'Need help or clarification', emoji: '❓', textColor: 'text-blue-700', bgColor: 'bg-blue-100' },
};
